import React from 'react';
import { Flex, xcss, Stack, Inline, Box } from '@atlaskit/primitives';
import { JiraServiceManagementIcon } from '@atlaskit/temp-nav-app-icons/jira-service-management';
import Heading from '@atlaskit/heading';

import { Section } from '@atlaskit/menu';
import { PremiumLandingPageEntryPointButtonWithDrawer } from '@atlassian/jira-servicedesk-premium-landing-page-buttons/src/ui/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { PremiumLandingPageOrigin } from '@atlassian/jira-servicedesk-premium-landing-page/src/ui/types.tsx';
import { UpsellButton } from '@atlassian/jira-servicedesk-premium-landing-page/src/ui/upsell-button/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useProductAndEdition } from '@atlassian/jira-navigation-apps-sidebar-edition-awareness/src/services/use-product-and-edition.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { messages } from './messages.tsx';

type PremiumLandingPageTriggerBannerProps = {
	origin: PremiumLandingPageOrigin;
};

export const PremiumLandingPageTriggerBanner = ({
	origin,
}: PremiumLandingPageTriggerBannerProps) => {
	const { formatMessage } = useIntl();
	const isSiteAdmin = useIsSiteAdmin();
	const { product } = useProductAndEdition();

	if (!isSiteAdmin || product !== 'jira-service-management') {
		return null;
	}

	const mainContent = () => (
		<Inline space="space.100" alignBlock="start" alignInline="start">
			<JiraServiceManagementIcon />
			<Stack space="space.075" xcss={contentWrapperStyles}>
				<Box xcss={headingStyles}>
					<Heading as="h2" size="xsmall">
						{formatMessage(messages.subtitleLabel)}
					</Heading>
				</Box>
				<Flex justifyContent="start" gap="space.100" xcss={buttonsWrapperStyles}>
					<UpsellButton analyticsOrigin={origin} buttonLabel={messages.tryItFreeButtonLabel} />
					<PremiumLandingPageEntryPointButtonWithDrawer origin={origin} />
				</Flex>
			</Stack>
		</Inline>
	);

	return (
		<JSErrorBoundary
			id="jsmPremiumLandingPageTriggerBanner"
			packageName="jiraServiceDeskPremiumLandingPageTriggerBanner"
			fallback="flag"
		>
			<Section hasSeparator>
				<Flex xcss={containerStyles}>{mainContent()}</Flex>
			</Section>
		</JSErrorBoundary>
	);
};

const containerStyles = xcss({
	backgroundColor: 'elevation.surface.overlay',
	padding: 'space.200',
	maxWidth: '496px',
});

const contentWrapperStyles = xcss({
	width: '100%',
	alignItems: 'flex-start',
});

const buttonsWrapperStyles = xcss({
	width: '100%',
});

const headingStyles = xcss({
	alignItems: 'flex-start',
	textAlign: 'left',
	width: '100%',
});
